import { useConfig } from "./utils.js";
import {
  fetchCartQuery,
  cartCreateQuery,
  addToStorefrontCartQuery,
  removeFromStorefrontCartQuery,
  updateCartLineQuery,
} from "./cart--api-query.js";
import {
  convertStorefrontToAjaxFormat,
  convertAttributesFromAjaxToStorefront,
  convertAjaxCartAndAdd,
  setCookie,
  getCookie,
} from "./cart--api-utils.js";
import { writeCartMetaToCookie, eventNames } from "./cart.utils.js";
import { HIDDEN_PROPERTY_NAME, RUSH_FEE_KEY } from "./cart--api-utils.js";

/**
 * This helper function makes sure we're updating the `coreCart` cookie and updates the `window.cart` global object
 * @param {*} cart
 */
const dispatchWindowEvent = (cart) => {
  writeCartMetaToCookie(getCookie("cart-token"), {
    hasItemsInCart: cart.items.length > 0,
  });
  window.cart = cart;
};

/**
 * Sends the Storefront API request with query and variables and checks for errors
 * @query {string} GraphQL query request
 * @variables {Array} Additional variables, optional.
 * @returns {Object | null}
 */

export const sendStorefrontRequest = async ({ query, variables }) => {
  const { shop } = window.Shopify;
  const { storefrontApiAccessToken, storefrontApiVersion } = useConfig();
  const apiVersion = storefrontApiVersion;
  const accessToken = storefrontApiAccessToken;
  const url = `https://${shop}/api/${apiVersion}/graphql.json`;

  try {
    const result = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": accessToken,
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!result.ok) {
      return console.error(result);
    }

    const { data } = await result.json();
    return data;
  } catch (e) {
    console.error("Error in sending Storefront API request", e);
    return {
      statusCode: 500,
      body: JSON.stringify({
        message: "Error in sending Storefront API request",
      }),
    };
  }
};

/**
 * Uses the Storefront API to create a cart. After creating cart, we take the cart id and store into cookies.
 * @returns
 */
const createCartToken = async () => {
  try {
    const data = await sendStorefrontRequest({
      query: cartCreateQuery,
    });
    if (data?.cartCreate) {
      const { id } = data.cartCreate.cart;
      const updatedId = id.split("/Cart/").pop();
      setCookie("cart-token", updatedId);
      return updatedId;
    }
    // If we don't get data back, pause and try again
    setTimeout(async () => {
      const data = await sendStorefrontRequest({
        query: cartCreateQuery,
      });
      const { id } = data.cartCreate.cart;
      const retryUpdatedId = id.split("/Cart/").pop();
      setCookie("cart-token", retryUpdatedId);
      return retryUpdatedId;
    }, 500);
  } catch (error) {
    console.error("Error creating cart:", error);
    return {
      statusCode: 500,
      body: JSON.stringify({ message: "There was a problem creating a cart." }),
    };
  }
};

/**
 * Checks for 'cart-token' cookie and fetches the Storefront API cart, then stores into window.
 * @returns Storefront API cart
 */
export const fetchStorefrontCart = async () => {
  try {
    const cartToken =
      getCookie("cart-token") &&
      `gid://shopify/Cart/${getCookie("cart-token")}`;
    if (!cartToken && !window.location.hostname.includes("myshopify")) {
      await createCartToken();
      await fetchStorefrontCart();
      return;
    }

    const data = await sendStorefrontRequest({
      query: fetchCartQuery(cartToken),
    });

    if (!data.cart && !window.location.hostname.includes("myshopify")) {
      setCookie("cart-token", "", -1);
      await createCartToken();
      await fetchStorefrontCart();
      return;
    }

    const cart = convertStorefrontToAjaxFormat(data.cart);
    setCookie("cart-checkout", cart.checkoutUrl);
    window.cart = cart;
    return cart;
  } catch (error) {
    console.error("Error fetching storefront cart:", error);
    return {
      statusCode: 500,
      body: JSON.stringify({
        message: "There was an error fetching the cart.",
        error: error.message,
      }),
    };
  }
};

/**
 * Helper function for `addToStorefrontCart` after checking the newly added item is a Dual Sale product, so we make a Storefront API call based on the product's "assigned" Rush Fee product stored in `rush-fee-data` component on `main-product.liquid`
 * @param {*} dualSaleItem
 * @param {*} quantity
 * @returns API data
 */
export const addRushFee = async (dualSaleItem, quantity) => {
  try {
    const cartToken = window.cart.token;
    // Rush Fee Variant IDs
    const rushFeeData = document.getElementById("rush-fee-variant");
    const { rushVariantId, defaultRushVariantId } = rushFeeData?.dataset || {};

    const {
      id,
      product_title: productTitle,
      variant_title: variantTitle,
      variant_options: variantOptions,
      key,
      properties,
    } = dualSaleItem;

    // Assign associated properties to make sure Rush Fee product aligns with Dual Sale product cart actions.
    const attributes = [
      {
        _productName: productTitle,
      },
      {
        _original_variant_id: id,
      },
      { _productNameWithoutColor: productTitle.split("-")[0] },
      { _variantName: variantTitle },
      {
        _variantNameWithoutArrival: variantOptions
          .flat()
          .filter((opt) => !opt.includes("Rush Production"))
          .join(" / "),
      },
    ];

    const variables = {
      cartId: cartToken,
      lines: [
        {
          merchandiseId: `gid://shopify/ProductVariant/${rushVariantId || defaultRushVariantId}`,
          quantity,
          attributes: convertAttributesFromAjaxToStorefront(attributes),
        },
      ],
    };

    // Send the Storefront API request to add Rush Fee product
    const data = await sendStorefrontRequest({
      query: addToStorefrontCartQuery,
      variables,
    });

    const converted = convertStorefrontToAjaxFormat(data.cartLinesAdd.cart);

    // Update the dual sale item with the rush fee price associated with it to be used for rendering Suits Cart rush fee UI
    const foundRushFeeItem = converted.items.find(
      (item) =>
        item.properties[HIDDEN_PROPERTY_NAME] &&
        item.properties[HIDDEN_PROPERTY_NAME] === id,
    );

    // Persist current line item properties
    const dualSaleAttributes = convertAttributesFromAjaxToStorefront([
      properties,
    ]);

    // Add a hidden line item attribute with rush fee price
    dualSaleAttributes.push({
      key: RUSH_FEE_KEY,
      value: JSON.stringify(foundRushFeeItem.price / 100),
    });

    // Send update for the dual sale cart line item
    const updatedDualSaleCart = await sendStorefrontRequest({
      query: updateCartLineQuery,
      variables: {
        cartId: cartToken,
        lines: [
          {
            id: key,
            merchandiseId: id,
            attributes: dualSaleAttributes,
          },
        ],
      },
    });

    return convertStorefrontToAjaxFormat(
      updatedDualSaleCart.cartLinesUpdate.cart,
    );
  } catch (error) {
    console.error("Error adding rush fee to dual sale item:", error);
  }
};

/**
 * Add to cart function attached to `window.atcClick` and other ATC functionality in `scripts.js`. We take in the item information from the button.dataset and item prop to format variables to be sent.
 * @param {*} item
 * @param {*} addToCartButton
 * @returns
 */
export const addToStorefrontCart = async (item, button) => {
  try {
    const addToCartButton = button;
    const { merchandiseId, attributes, quantity } = item.lines[0];
    const cartToken = window.cart.token;
    const buttonHTML = addToCartButton.innerHTML;
    const isSwatchCarousel = addToCartButton?.dataset?.location?.includes(
      "utm swatch carousel",
    );
    addToCartButton.innerHTML = "Adding &hellip;";

    // Check if 'mini-cart' or 'quick-add' is in attributes
    const isQuickAdd = attributes.some(
      (attribute) =>
        attribute.value === "mini-cart" || attribute.value === "quick-add",
    );

    const id = isQuickAdd
      ? `gid://shopify/ProductVariant/${merchandiseId}`
      : merchandiseId;

    const variables = {
      cartId: cartToken,
      lines: [
        {
          merchandiseId: id,
          quantity,
          attributes: convertAttributesFromAjaxToStorefront(attributes),
        },
      ],
    };

    let cartResponse = await sendStorefrontRequest({
      query: addToStorefrontCartQuery,
      variables,
    });

    // Error handling
    if (
      !cartResponse.cartLinesAdd.cart &&
      cartResponse.cartLinesAdd.userErrors
    ) {
      const errorContainer = document.querySelector(
        ".atc-error-message-container",
      );
      const errorMessage = document.querySelector("#atc-error-message");

      if (errorContainer) {
        errorMessage.innerText =
          cartResponse.cartLinesAdd.userErrors[0].message;
        errorContainer.classList.remove("hidden");
        addToCartButton.innerText = "Error. Try again!";
        setTimeout(() => {
          addToCartButton.innerText = "Add to Bag";
        }, 2500);
      }
      return console.error("Error adding to cart.");
    }

    // Format the cart response
    let updatedCart = convertStorefrontToAjaxFormat(
      cartResponse.cartLinesAdd.cart,
    );

    // Find the new added item
    const addedItems = {
      items: [updatedCart.items.find((newItem) => newItem.id === id)],
    };

    // Check if this cart includes a dual sale product in the newly added items
    const isDualSaleProduct = addedItems.items.find((product) =>
      product.variant_title.includes("Rush Production"),
    );

    // If there is a dual sale item, add the rush fee product
    if (isDualSaleProduct) {
      updatedCart = await addRushFee(isDualSaleProduct, quantity);
    }

    // If no errors, run through ATC button updates
    let innerText = "Add to Bag";
    if (
      addedItems.items[0].properties &&
      ("PREORDER" in addedItems.items[0].properties ||
        "_PREORDER" in addedItems.items[0].properties)
    ) {
      innerText = "Add to Bag";
    } else if (isSwatchCarousel) {
      innerText = buttonHTML;
    }

    if (addToCartButton.classList.contains("add-color-swatch")) {
      addToCartButton.innerText = `GET COLOR SWATCH`;
    }
    if (addToCartButton.id && addToCartButton.id === "personalization-atc") {
      window.closePersonalizationModal();
    }
    addToCartButton.innerHTML = innerText;

    // Prepare Klaviyo data if needed
    let klaviyoProduct = null;
    if (addToCartButton.classList.contains("quick-atc")) {
      const { dataset } = addToCartButton;
      klaviyoProduct = {
        Name: dataset.atcName,
        ProductID: dataset.atcProductid,
        Categories: dataset.atcCategories,
        ImageURL: dataset.atcImageurl,
        URL: dataset.atcUrl,
        Brand: dataset.atcBrand,
        Price: dataset.atcPrice,
        CompareAtPrice: dataset.atcCompareatprice,
        QuickATC: true,
      };
    }
    addToCartButton.dispatchEvent(new Event("cartChange", { bubbles: true }));
    window.dispatchEvent(
      new CustomEvent(eventNames.ADD_ITEM, {
        detail: {
          data: {
            addToCartResponse: addedItems,
            klaviyoProduct,
          },
          action: "add",
        },
      }),
    );
    dispatchWindowEvent(updatedCart);
    return updatedCart;
  } catch (error) {
    console.error("Error adding to storefront cart:", error);
    return {
      statusCode: 500,
      body: JSON.stringify({
        message: error.message || "There was a problem adding product to cart.",
      }),
    };
  }
};

/**
 * Remove from cart function that takes in the item information. Required information for API requests: variant ids.
 * @param {*} item
 * @returns
 */
export const removeFromStorefrontCart = async (item) => {
  try {
    const cartToken = window.cart.token;
    const lineIds = [item.id];
    const cart = window.cart.items;
    const foundRushFee = cart.find(
      (lineItem) =>
        lineItem.properties[HIDDEN_PROPERTY_NAME] === item.variantId &&
        lineItem.product_title.includes("Rush"),
    );

    if (foundRushFee) lineIds.push(foundRushFee.key);
    const variables = {
      cartId: cartToken,
      lineIds,
    };
    const data = await sendStorefrontRequest({
      query: removeFromStorefrontCartQuery,
      variables,
    });

    const updatedCart = convertStorefrontToAjaxFormat(
      data.cartLinesRemove.cart,
    );
    window.dispatchEvent(
      new CustomEvent(eventNames.REMOVE_ITEM, {
        detail: {
          data: {
            id: item.id,
            quantity: 0,
            cart: updatedCart,
          },
        },
      }),
    );
    dispatchWindowEvent(updatedCart);
    return updatedCart;
  } catch (error) {
    console.error("Error adding to storefront cart:", error);
    return {
      statusCode: 500,
      body: JSON.stringify({
        message: error.message || "There was a problem adding product to cart.",
      }),
    };
  }
};

/**
 * Update cart line quantity function.
 * @param {*} item
 * @returns
 */
export const updateCartLine = async (item) => {
  try {
    const { cart } = window;
    const { type, variantId, key } = item.dataset;
    const errorMessage = document.getElementById(
      `${item.dataset.keyPrefix}availability-error-${item.dataset.key}`,
    );
    const foundItem = cart.items.find((lineItem) => lineItem.key === key);
    const quantity =
      type === "increment" ? foundItem.quantity + 1 : foundItem.quantity - 1;
    const variables = {
      cartId: cart.token,
      lines: [
        {
          id: key,
          merchandiseId: variantId,
          quantity,
        },
      ],
    };

    // Update rush fee product quantity
    const isDualSaleProduct =
      foundItem.variant_title.includes("Rush Production");

    if (isDualSaleProduct) {
      const associatedRushFee = cart.items.find(
        (lineItem) =>
          lineItem.product_title.includes("Rush Fee") &&
          lineItem.properties[HIDDEN_PROPERTY_NAME] === foundItem.id,
      );
      const { id, key: associatedRushFeeKey } = associatedRushFee;
      variables.lines.push({
        id: associatedRushFeeKey,
        merchandiseId: id,
        quantity,
      });
    }

    const data = await sendStorefrontRequest({
      query: updateCartLineQuery,
      variables,
    });
    if (
      data.cartLinesUpdate.userErrors.length > 0 &&
      data.cartLinesUpdate.userErrors[0].field.includes("quantity")
    ) {
      errorMessage.classList.remove("hidden");
      return;
    }
    const updatedCart = convertStorefrontToAjaxFormat(
      data.cartLinesUpdate.cart,
    );
    window.dispatchEvent(
      new CustomEvent(eventNames.CART_DATA_UPDATED, {
        detail: { cart: updatedCart },
      }),
    );
    dispatchWindowEvent(updatedCart);
    return updatedCart;
  } catch (error) {
    console.error("Error updating cart item in storefront cart:", error);
    return {
      statusCode: 500,
      body: JSON.stringify({
        message:
          error.message || "There was a problem updating cart item in cart.",
      }),
    };
  }
};

/**
 * Audit misaligned rush fee cart line items and removes or adjusts quantity to match with associated dual sale cart line items.
 * @param {*} cart
 */
const auditRushFeesForStorefrontCart = async (cart) => {
  let cartChanged = false;
  try {
    const promises = cart.items.map(async (item) => {
      if (item.product_title.includes("Rush Fee")) {
        /**
         * Find associated product and update the quantity if there's a diff
         *
         * Use the key for the rush fee in case multiple fees exist for different products
         */
        const { key, id, quantity } = item;
        const associatedProductLineItemId =
          item.properties[HIDDEN_PROPERTY_NAME];

        /**
         * There are two scenarios the customer may encounter:
         *
         * 1) Customer orders X amount but now only Y amount are available -> we swap in the Y quantity to Rush Fee
         * 2) Customer orders X amount but now 0 are available -> we still swap in the Y quantity to Rush Fee
         */
        const dualSaleProductQty = cart.items.find(
          (product) => product.id === associatedProductLineItemId,
        )?.quantity;
        if (quantity !== dualSaleProductQty) {
          const variables = {
            cartId: cart.token,
            lines: [
              {
                id: key,
                merchandiseId: id,
                quantity: !dualSaleProductQty ? 0 : dualSaleProductQty,
              },
            ],
          };
          const success = await sendStorefrontRequest({
            query: updateCartLineQuery,
            variables,
          });
          if (success) cartChanged = true;
        }
      }
    });

    await Promise.all(promises);

    /**
     * Reload the page if updates were successful.
     * Use date time search param in URL to cache bust.
     */
    if (cartChanged) {
      const url = new URL(window.location.href);
      url.searchParams.set("_", new Date().getTime());
      window.location.replace(url.toString());
    }
  } catch (error) {
    console.error("Error auditing items in the cart:", error);
  }
};

/**
 * This function is called when there is an AJAX cart AND the Storefront API feature flag is true. We want to grab the array of product variants with associated attributes and add to the Storefront Cart. After successfully storing, we will remove the AJAX cart information and clear the cart.
 * @returns
 */
export const handleAjaxCart = async () => {
  const cartCookie = getCookie("cart-token") || (await createCartToken());
  const cartToken = `gid://shopify/Cart/${cartCookie}`;
  const ajaxCart = await fetch("/cart.js").then((res) => res.json());

  // Check if there are items in an AJAX cart
  if (ajaxCart.items.length > 0) {
    // If there are cart items, convert to Storefront format
    const convertedAjaxCart = convertAjaxCartAndAdd(ajaxCart.items);
    const variables = {
      cartId: cartToken,
      lines: convertedAjaxCart,
    };
    // Send a request to update the cart.
    try {
      const data = await sendStorefrontRequest({
        query: addToStorefrontCartQuery,
        variables,
      });

      const convertedCart = convertStorefrontToAjaxFormat(
        data.cartLinesAdd.cart,
      );
      dispatchWindowEvent(convertedCart);
      await window.buildMiniCart();

      // Clear the AJAX cart
      try {
        const response = await fetch("/cart/clear", { method: "POST" });
        if (!response.ok) {
          console.error(
            `Error clearing cart: ${response.status} ${response.statusText}`,
          );
          return;
        }
        await response.json().catch(() => null);
      } catch (error) {
        console.error("Network error clearing AJAX cart:", error);
      }
    } catch (error) {
      console.error("Error handling AJAX to Storefront conversion:", error);
    }
  }
  await fetchStorefrontCart();
};

/**
 * Checks for Statsig feature flag to use the Storefront or Ajax Cart. Based on the flag, we store the cart into window.
 */
const setGlobalCart = async () => {
  await handleAjaxCart();
  window.buildMiniCart();
};

/**
 * Async function that calls the cart promises.
 */
const initiateCart = async () => {
  await setGlobalCart();
  await auditRushFeesForStorefrontCart(window.cart);
};

/**
 * Initiate cart if there is no cart in window.
 */
if (!window.cart) {
  initiateCart();
}

window.fetchStorefrontCart = fetchStorefrontCart;
window.addToStorefrontCart = addToStorefrontCart;
window.removeFromStorefrontCart = removeFromStorefrontCart;
window.updateCartLine = updateCartLine;
window.setGlobalCart = setGlobalCart;
